import { useState, useEffect } from 'preact/hooks';
import { getLeaves, approveLeave, rejectLeave, cancelLeave, createLeave, getLeaveTypes, getUsers } from '../lib/api';

export function RequestsPage({ user, workspace }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [requestForm, setRequestForm] = useState({
    user_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    hours: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get the selected leave type to check if it's hours-based
  const selectedLeaveType = leaveTypes.find(lt => lt.id === requestForm.leave_type_id);
  const isHoursBased = selectedLeaveType?.unit === 'hours';

  // Get the selected user's hours_per_day for auto-calculation
  const selectedUser = users.find(u => u.id === requestForm.user_id);
  const hoursPerDay = selectedUser?.hours_per_day || 8;

  // Calculate working days between dates (simple client-side calculation)
  function calculateWorkingDays(startDate, endDate) {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      // Count Mon-Fri by default (server will do proper calculation with holidays)
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }

  // Calculate hours from time difference
  function calculateHoursFromTime(startTime, endTime) {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const diffMinutes = endMinutes - startMinutes;
    return Math.max(0, Math.round(diffMinutes / 60)); // Round to whole hours
  }

  // Auto-calculate hours when dates/times change for hours-based leave
  const calculatedWorkingDays = calculateWorkingDays(requestForm.start_date, requestForm.end_date || requestForm.start_date);
  const isSingleDay = !requestForm.end_date || requestForm.start_date === requestForm.end_date;
  const calculatedHoursFromTime = calculateHoursFromTime(requestForm.start_time, requestForm.end_time);

  // For single day: use time difference; for multi-day: use working days × hours_per_day
  const calculatedHours = isSingleDay ? calculatedHoursFromTime : calculatedWorkingDays * hoursPerDay;

  useEffect(() => {
    loadRequests();
  }, [filter]);

  // Auto-update hours when dates/times change (for hours-based leave)
  useEffect(() => {
    if (isHoursBased && requestForm.start_date) {
      const singleDay = !requestForm.end_date || requestForm.start_date === requestForm.end_date;
      let hours;
      if (singleDay) {
        hours = calculateHoursFromTime(requestForm.start_time, requestForm.end_time);
      } else {
        const days = calculateWorkingDays(requestForm.start_date, requestForm.end_date);
        hours = days * hoursPerDay;
      }
      if (hours > 0 && requestForm.hours !== String(hours)) {
        setRequestForm(prev => ({ ...prev, hours: String(hours) }));
      }
    }
  }, [requestForm.start_date, requestForm.end_date, requestForm.start_time, requestForm.end_time, requestForm.user_id, isHoursBased, hoursPerDay]);

  async function openRequestModal() {
    try {
      const [typesData, usersData] = await Promise.all([
        getLeaveTypes(),
        getUsers()
      ]);
      setLeaveTypes(typesData);
      setUsers(usersData);
      setRequestForm({
        user_id: user?.user_id || '',
        leave_type_id: typesData[0]?.id || '',
        start_date: '',
        end_date: '',
        start_time: '09:00',
        end_time: '17:00',
        hours: '',
        reason: ''
      });
      setShowRequestModal(true);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }

  async function handleSubmitRequest(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const leaveData = {
        user_id: requestForm.user_id,
        leave_type_id: requestForm.leave_type_id,
        start_date: requestForm.start_date,
        end_date: requestForm.end_date || requestForm.start_date,
        reason: requestForm.reason
      };

      // Add hours and times if this is an hours-based leave type
      if (isHoursBased && requestForm.hours) {
        leaveData.hours = parseInt(requestForm.hours, 10);
        leaveData.start_time = requestForm.start_time;
        leaveData.end_time = requestForm.end_time;
      }

      await createLeave(leaveData);
      setShowRequestModal(false);
      loadRequests();
    } catch (error) {
      console.error('Failed to create leave:', error);
      alert(error.message || 'Failed to create leave request');
    } finally {
      setSubmitting(false);
    }
  }

  async function loadRequests() {
    setLoading(true);
    try {
      const data = await getLeaves(filter !== 'all' ? { status: filter } : {});
      setRequests(data);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(requestId) {
    try {
      await approveLeave(requestId);
      loadRequests();
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleReject() {
    if (!showRejectModal) return;
    try {
      await rejectLeave(showRejectModal, rejectReason);
      setShowRejectModal(null);
      setRejectReason('');
      loadRequests();
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('Failed to reject: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleCancel(requestId) {
    if (!confirm('Are you sure you want to cancel this leave? If already approved, the balance will be restored.')) return;
    try {
      await cancelLeave(requestId);
      loadRequests();
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('Failed to cancel: ' + (error.message || 'Unknown error'));
    }
  }

  const isApprover = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'approver';

  return (
    <div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
        <h2 style="font-size: 1.5rem; font-weight: 600;">Leave Requests</h2>
        <button class="btn btn-primary" onClick={openRequestModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Request Leave
        </button>
      </div>

      {/* Filter Tabs */}
      <div style="display: flex; gap: 0.5rem; margin-bottom: 1.5rem;">
        {['pending', 'approved', 'rejected', 'all'].map(status => (
          <button
            key={status}
            class={`btn ${filter === status ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(status)}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div class="card">
        <div class="card-body">
          {loading ? (
            <div class="loading-screen" style="min-height: 200px;">
              <div class="spinner" />
            </div>
          ) : requests.length > 0 ? (
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Reason</th>
                    {isApprover && (filter === 'pending' || filter === 'approved' || filter === 'all') && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request.id}>
                      <td>
                        <div style="display: flex; align-items: center; gap: 0.5rem;">
                          <div class="avatar">
                            {request.avatar_url ? (
                              <img src={request.avatar_url} alt="" />
                            ) : (
                              request.user_name?.charAt(0) || '?'
                            )}
                          </div>
                          {request.user_name}
                        </div>
                      </td>
                      <td>
                        <span style="display: flex; align-items: center; gap: 0.25rem;">
                          {request.icon} {request.leave_type_name}
                        </span>
                      </td>
                      <td>
                        {request.start_date === request.end_date
                          ? request.start_date
                          : `${request.start_date} - ${request.end_date}`}
                      </td>
                      <td>
                        {(() => {
                          const unit = request.leave_type_unit || 'days';
                          const val = request.duration_days;
                          const plural = val !== 1 ? 's' : '';
                          if (unit === 'hours') return `${val} hour${plural}`;
                          if (unit === 'weeks') return `${val} week${plural}`;
                          if (unit === 'months') return `${val} month${plural}`;
                          return `${val} day${plural}`;
                        })()}
                      </td>
                      <td>
                        <span class={`badge badge-${request.status}`}>
                          {request.status}
                        </span>
                      </td>
                      <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
                        {request.reason || '-'}
                      </td>
                      {isApprover && (filter === 'pending' || filter === 'approved' || filter === 'all') && (
                        <td>
                          <div style="display: flex; gap: 0.5rem;">
                            {request.status === 'pending' && (
                              <>
                                <button
                                  class="btn btn-sm btn-success"
                                  onClick={() => handleApprove(request.id)}
                                >
                                  Approve
                                </button>
                                <button
                                  class="btn btn-sm btn-danger"
                                  onClick={() => setShowRejectModal(request.id)}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {(request.status === 'approved' || request.status === 'pending') && (
                              <button
                                class="btn btn-sm btn-secondary"
                                onClick={() => handleCancel(request.id)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div class="empty-state">
              <div class="empty-state-icon">📋</div>
              <h3>No requests found</h3>
              <p>
                {filter === 'pending'
                  ? 'No pending requests to review.'
                  : 'No leave requests match your filter.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div class="modal-overlay" onClick={() => setShowRejectModal(null)}>
          <div class="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h3>Reject Request</h3>
              <button class="modal-close" onClick={() => setShowRejectModal(null)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div class="form-group">
                <label class="form-label">Reason for rejection</label>
                <textarea
                  class="form-input"
                  rows="4"
                  placeholder="Please provide a reason..."
                  value={rejectReason}
                  onInput={(e) => setRejectReason(e.target.value)}
                />
              </div>
            </div>
            <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
              <button class="btn btn-secondary" onClick={() => setShowRejectModal(null)}>
                Cancel
              </button>
              <button class="btn btn-danger" onClick={handleReject}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Leave Modal */}
      {showRequestModal && (
        <div class="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div class="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h3>Request Leave</h3>
              <button class="modal-close" onClick={() => setShowRequestModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmitRequest} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {(user?.role === 'admin' || user?.role === 'owner') && (
                  <div class="form-group">
                    <label class="form-label">Employee</label>
                    <select
                      class="form-input"
                      value={requestForm.user_id}
                      onChange={(e) => setRequestForm({ ...requestForm, user_id: e.target.value })}
                      required
                    >
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div class="form-group">
                  <label class="form-label">Leave Type</label>
                  <select
                    class="form-input"
                    value={requestForm.leave_type_id}
                    onChange={(e) => setRequestForm({ ...requestForm, leave_type_id: e.target.value })}
                    required
                  >
                    {leaveTypes.map(lt => (
                      <option key={lt.id} value={lt.id}>{lt.icon} {lt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date range - same for both hours and days-based leave */}
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                  <div class="form-group">
                    <label class="form-label">Start Date</label>
                    <input
                      type="date"
                      class="form-input"
                      value={requestForm.start_date}
                      onInput={(e) => setRequestForm({ ...requestForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">End Date</label>
                    <input
                      type="date"
                      class="form-input"
                      value={requestForm.end_date}
                      onInput={(e) => setRequestForm({ ...requestForm, end_date: e.target.value })}
                      min={requestForm.start_date}
                    />
                  </div>
                </div>

                {/* Time range for hours-based leave types */}
                {isHoursBased && (
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                    <div class="form-group">
                      <label class="form-label">Start Time</label>
                      <input
                        type="time"
                        class="form-input"
                        value={requestForm.start_time}
                        onInput={(e) => setRequestForm({ ...requestForm, start_time: e.target.value })}
                        step="1800"
                        required
                      />
                    </div>
                    <div class="form-group">
                      <label class="form-label">End Time</label>
                      <input
                        type="time"
                        class="form-input"
                        value={requestForm.end_time}
                        onInput={(e) => setRequestForm({ ...requestForm, end_time: e.target.value })}
                        step="1800"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Hours input for hours-based leave types */}
                {isHoursBased && (
                  <div class="form-group">
                    <label class="form-label">Total Hours</label>
                    <div style="display: flex; align-items: center; gap: 0.75rem;">
                      <input
                        type="number"
                        class="form-input"
                        style="width: 100px;"
                        value={requestForm.hours}
                        onInput={(e) => setRequestForm({ ...requestForm, hours: e.target.value })}
                        min="1"
                        step="1"
                        placeholder="Hours"
                        required
                      />
                      <span style="color: var(--text-secondary); font-size: 0.875rem;">
                        {isSingleDay ? (
                          requestForm.start_time && requestForm.end_time && (
                            <>({requestForm.start_time} to {requestForm.end_time} = {calculatedHoursFromTime}h)</>
                          )
                        ) : (
                          calculatedWorkingDays > 0 && (
                            <>({calculatedWorkingDays} day{calculatedWorkingDays !== 1 ? 's' : ''} × {hoursPerDay}h = {calculatedHours}h)</>
                          )
                        )}
                      </span>
                    </div>
                    <p class="form-hint" style="margin-top: 0.25rem;">
                      {isSingleDay
                        ? 'Calculated from time range. Adjust if needed.'
                        : 'For multi-day leave, times apply to first/last day. Adjust total if needed.'}
                    </p>
                  </div>
                )}

                <div class="form-group">
                  <label class="form-label">Reason (optional)</label>
                  <textarea
                    class="form-input"
                    rows="3"
                    placeholder="Reason for leave request..."
                    value={requestForm.reason}
                    onInput={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                  />
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowRequestModal(false)}>
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
